import './TestStoreList.css';
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { navigationBarState } from '../../states/NavigationBar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TestStoreItem, { TestStoreItemSkeleton } from './TestStoreItem';

function TestStoreList() {
    const navigate = useNavigate();
    const setNavigationBarState = useSetRecoilState(navigationBarState);
    const [loading, setLoading] = useState(true);
    const [levelTestInfo, setLevelTestInfo] = useState(null);
    const [recommendedTests, setRecommendedTests] = useState([]);
    const [allTests, setAllTests] = useState([]);
    const [pressedBannerId, setPressedBannerId] = useState(null);
    const pressTimeoutRef = useRef(null);

    const hasCompletedLevelTest = levelTestInfo?.status === 'EVALUATED';

    useEffect(() => {
        window.scrollTo(0, 0);
        setNavigationBarState({
            title: '시험신청',
            'left-item': { type: 'back' },
        });
        fetchTestStore();
    }, [setNavigationBarState]);

    const fetchTestStore = async () => {
        try {
            const response = await axios.get('/tests/store/');
            setLevelTestInfo(response.data.level_test);
            setRecommendedTests(response.data.recommended_tests || []);
            setAllTests(response.data.all_tests || []);
        } catch (error) {
            console.error('Failed to fetch test store:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBannerPress = (id, path) => {
        setPressedBannerId(id);
        if (pressTimeoutRef.current) {
            clearTimeout(pressTimeoutRef.current);
        }
        pressTimeoutRef.current = setTimeout(() => {
            setPressedBannerId(null);
            navigate(path);
        }, 80);
    };

    const handleTestClick = (test) => {
        navigate('/test-store/application', { state: { test } });
    };

    const getRecommendedClassName = () => {
        if (recommendedTests.length > 0 && recommendedTests[0].academy_class_name) {
            return recommendedTests[0].academy_class_name;
        }
        return null;
    };

    const renderLevelTestSection = () => {
        if (loading) return null;

        // 미등록 - 신청 배너 표시
        if (!levelTestInfo) {
            return (
                <div
                    className={`test-store-level-banner ${pressedBannerId === 'level-test' ? 'pressed' : ''}`}
                    onClick={() => handleBannerPress('level-test', '/test-store/level-test')}
                >
                    <div className="test-store-level-banner-left">
                        <span className="test-store-level-banner-icon">🎯</span>
                        <div className="test-store-level-banner-content">
                            <span className="test-store-level-banner-title">레벨테스트</span>
                            <span className="test-store-level-banner-desc">내 레벨에 맞는 시험 찾기</span>
                        </div>
                    </div>
                    <div className="test-store-level-banner-right">
                        <span className="test-store-level-banner-badge">무료</span>
                        <span className="test-store-level-banner-arrow">
                            <ChevronRightIcon />
                        </span>
                    </div>
                </div>
            );
        }

        // 완료 - 성적 카드 표시
        if (hasCompletedLevelTest) {
            return (
                <div className="test-store-level-result">
                    <div className="test-store-level-result-header">
                        <span className="test-store-level-result-title">레벨테스트 결과</span>
                        <span
                            className="test-store-level-result-retake"
                            onClick={() => navigate('/test-store/level-test')}
                        >
                            재응시
                        </span>
                    </div>
                    <div className="test-store-level-result-score">
                        <span className="test-store-level-result-emoji">🎯</span>
                        <div className="test-store-level-result-info">
                            <div className="test-store-level-result-score-value">
                                <span className="test-store-level-result-score-number">{levelTestInfo.score}</span>
                                <span className="test-store-level-result-score-max">/ {levelTestInfo.max_score}점</span>
                            </div>
                            {getRecommendedClassName() && (
                                <span className="test-store-level-result-percentage">
                                    {getRecommendedClassName()} 수준
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // 진행 중 - 미노출
        return null;
    };

    const renderRecommendedSection = () => {
        if (!hasCompletedLevelTest) return null;

        return (
            <div className="test-store-section">
                <div className="test-store-section-label">
                    추천 시험{recommendedTests.length > 0 ? ` (${recommendedTests.length})` : ''}
                </div>
                <div className="test-store-section-desc">
                    레벨테스트 결과를 기반으로 추천드려요
                </div>
                {loading ? (
                    <div className="test-store-list">
                        <TestStoreItemSkeleton />
                    </div>
                ) : recommendedTests.length > 0 ? (
                    <div className="test-store-list">
                        {recommendedTests.map((test) => (
                            <TestStoreItem
                                key={test.id}
                                test={test}
                                isRecommended={true}
                                onClick={() => handleTestClick(test)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="test-store-empty">
                        <div className="test-store-empty-text">추천 가능한 시험이 없습니다</div>
                    </div>
                )}
            </div>
        );
    };

    const renderAllTestsSection = () => {
        return (
            <div className="test-store-section">
                <div className="test-store-section-label">
                    전체 시험{allTests.length > 0 ? ` (${allTests.length})` : ''}
                </div>
                {loading ? (
                    <div className="test-store-list">
                        <TestStoreItemSkeleton />
                        <TestStoreItemSkeleton />
                        <TestStoreItemSkeleton />
                    </div>
                ) : allTests.length > 0 ? (
                    <div className="test-store-list">
                        {allTests.map((test) => (
                            <TestStoreItem
                                key={test.id}
                                test={test}
                                onClick={() => handleTestClick(test)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="test-store-empty">
                        <div className="test-store-empty-text">신청 가능한 시험이 없습니다</div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="test-store-page">
            <div className="test-store-container">
                {renderLevelTestSection()}
                {renderRecommendedSection()}
                {renderAllTestsSection()}
            </div>
        </div>
    );
}

export default TestStoreList;
